import { IPriceBreakdownList, IWindowCartItem } from "@/features/assistant/types";
import { IQuoteLineItem } from '../types';
import { calculateLineItem } from "./calculateLineItem";
import { calculateVssLineItem } from "./calculateVssLineItem";
import { calculateVssTotals } from "./calculateVssTotals";
import { roundToTwoDecimals } from "./calculateQuoteTotals";

interface PricingUpdateParams {
  /** The updated window cart item after configuration edit */
  updatedItem: IWindowCartItem;
  /** All line items on the quote (used to recalculate quote totals) */
  allLineItems: IQuoteLineItem[];
  /** The quotedetailid being updated */
  updatedLineItemId: string;
  /** VAT % from the quote (normal pricing) */
  vatPercentage: number;
  /** Custom discount % — used only when discountType is 'percentage' */
  customDiscountPercentage: number;
  /** Fixed discount amount — used when discountType is 'amount' */
  customDiscountAmount?: number;
  /** Whether the discount is a fixed amount or a percentage of subtotal */
  discountType?: 'percentage' | 'amount';
  /** VAT % used for VSS pricing (from dogma_vssvatpercentage on the quote) */
  vssVatPercentage: number;
  /** Optional delivery-charge resolver (delivery is part of the VAT base) */
  getDeliveryCharge?: (subtotal: number) => number;
}

export interface LineItemUpdatePayload {
  quotedetailid: string;
  baseamount: number;
  priceperunit: number;
  extendedamount: number;
  tax: number;
  manualdiscountamount: number;
  dogma_vsssubtotal: number;
  dogma_vssdiscountamount: number;
  dogma_vssvatamount: number;
  dogma_vsstotalamount: number;
}

export interface QuoteUpdatePayload {
  quoteid?: string;
  totaldiscountamount: number;
  totaltax: number;
  totalamount: number;
  dogma_vsssubtotal: number;
  dogma_vssdiscountamount: number;
  dogma_vssvatamount: number;
  dogma_vsstotalamount: number;
  dogma_vssvatpercentage: number;
}

export interface PricingUpdateResult {
  lineItemPayload: LineItemUpdatePayload;
  quotePayload: QuoteUpdatePayload;
  subtotal: number;
  deliveryCharge: number;
  /**
   * Tax-only patches for the lines other than the edited one. Dataverse rolls
   * the standard line `tax` fields back up into the quote's `totaltax`, so the
   * delivery VAT must be spread across every line's `tax` — otherwise the
   * rollup undercounts against the delivery-inclusive totaltax above.
   */
  otherLineTaxUpdates: { quotedetailid: string; tax: number }[];
}

export const calculatePricingUpdate = ({
  updatedItem,
  allLineItems,
  updatedLineItemId,
  vatPercentage,
  customDiscountPercentage,
  customDiscountAmount,
  discountType,
  vssVatPercentage,
  getDeliveryCharge,
}: PricingUpdateParams): PricingUpdateResult => {
  // Compute quote subtotal first — needed for proportional discount on line items
  const allItemPrices = allLineItems.map((li) => {
    if (li.quotedetailid === updatedLineItemId) {
      return { totalPrice: updatedItem.price.totalPrice, quantity: updatedItem.quantity };
    }
    return { totalPrice: li.priceperunit || 0, quantity: li.quantity || 1 };
  });

  const subtotal = roundToTwoDecimals(
    allItemPrices.reduce((sum, i) => sum + roundToTwoDecimals(i.totalPrice * i.quantity), 0)
  );

  // Discount, delivery, and VAT are computed BEFORE the line-item payload so
  // the delivery VAT can be spread into each line's `tax` (see below).
  const totalDiscountAmount = discountType === 'amount' && customDiscountAmount !== undefined
    ? roundToTwoDecimals(customDiscountAmount)
    : roundToTwoDecimals(subtotal * (customDiscountPercentage / 100));
  const subtotalAfterDiscount = roundToTwoDecimals(subtotal - totalDiscountAmount);
  // Delivery charge is part of the VAT base, not added on top of it.
  const deliveryCharge = getDeliveryCharge ? getDeliveryCharge(subtotal) : 0;
  const vatBase = roundToTwoDecimals(subtotalAfterDiscount + deliveryCharge);
  const vat = roundToTwoDecimals(vatBase * (vatPercentage / 100));
  const totalPrice = roundToTwoDecimals(vatBase + vat);

  // Recalculate the updated line item using the new price from the edited cart item
  const lineItemCalc = calculateLineItem({
    itemPrice: updatedItem.price,
    quantity: updatedItem.quantity,
    customDiscountPercentage,
    customDiscountAmount,
    discountType,
    subtotal,
    vatPercentage,
  });

  // Recalculate VSS pricing for the updated line item
  const vssLineItemCalc = calculateVssLineItem({
    itemPrice: updatedItem.price,
    quantity: updatedItem.quantity,
    vatPercentage: vssVatPercentage,
  });

  // Dataverse rolls the standard line `tax` fields back up into the quote's
  // `totaltax`, so the delivery VAT must be spread across every line's `tax`
  // (proportionally to its share of subtotalAfterDiscount) — otherwise the
  // rollup undercounts against the delivery-inclusive totaltax above.
  const deliveryVatTotal = roundToTwoDecimals(deliveryCharge * (vatPercentage / 100));
  const deliveryVatShareFor = (extendedAmount: number): number =>
    subtotalAfterDiscount > 0
      ? roundToTwoDecimals((extendedAmount / subtotalAfterDiscount) * deliveryVatTotal)
      : 0;

  const lineItemPayload: LineItemUpdatePayload = {
    quotedetailid: updatedLineItemId,
    baseamount: updatedItem.price.totalPrice,
    priceperunit: lineItemCalc.itemTotalPrice,
    extendedamount: lineItemCalc.extendedAmount,
    tax: roundToTwoDecimals(lineItemCalc.taxAmount + deliveryVatShareFor(lineItemCalc.extendedAmount)),
    manualdiscountamount: lineItemCalc.totalDiscountAmount,
    dogma_vsssubtotal: vssLineItemCalc.vssExtendedAmount,
    dogma_vssdiscountamount: vssLineItemCalc.vssDiscountAmount,
    dogma_vssvatamount: vssLineItemCalc.vssVatAmount,
    dogma_vsstotalamount: vssLineItemCalc.vssLineTotal,
  };

  // The unchanged lines keep their prices, but their `tax` must be re-spread
  // with the delivery VAT so the quote-level totaltax rollup stays consistent
  // with `vat` above. Only `tax` is patched — everything else is left alone.
  const otherLineTaxUpdates = allLineItems
    .filter((li) => li.quotedetailid !== updatedLineItemId)
    .map((li) => {
      const calc = calculateLineItem({
        itemPrice: { totalPrice: li.priceperunit || 0 } as IPriceBreakdownList,
        quantity: li.quantity || 1,
        customDiscountPercentage,
        customDiscountAmount,
        discountType,
        subtotal,
        vatPercentage,
      });
      return {
        quotedetailid: li.quotedetailid,
        tax: roundToTwoDecimals(calc.taxAmount + deliveryVatShareFor(calc.extendedAmount)),
      };
    });

  // Recalculate quote-level VSS totals across all line items
  // For the updated item use updatedItem.price; for others reconstruct per-unit VSS price from stored data
  const allVssItems = allLineItems.map((li) => {
    if (li.quotedetailid === updatedLineItemId) {
      return { price: updatedItem.price, quantity: updatedItem.quantity };
    }
    const qty = li.quantity || 1;
    return {
      price: {
        vssTotalPrice: roundToTwoDecimals((li.dogma_vsssubtotal || 0) / qty),
        totalVssDiscountAmount: roundToTwoDecimals((li.dogma_vssdiscountamount || 0) / qty),
      } as IPriceBreakdownList,
      quantity: qty,
    };
  });

  const vssTotals = calculateVssTotals({ cartItems: allVssItems, vatPercentage: vssVatPercentage, deliveryCharge });

  const quotePayload: QuoteUpdatePayload = {
    totaldiscountamount: totalDiscountAmount,
    totaltax: vat,
    totalamount: totalPrice,
    dogma_vsssubtotal: vssTotals.vssSubtotal,
    dogma_vssdiscountamount: vssTotals.vssDiscountAmount,
    dogma_vssvatamount: vssTotals.vssVat,
    dogma_vsstotalamount: vssTotals.vssTotalAmount,
    dogma_vssvatpercentage: vssVatPercentage,
  };

  return { lineItemPayload, quotePayload, subtotal, deliveryCharge, otherLineTaxUpdates };
};
